import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Avatar,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import axiosInstance from '../../helpers/axios'
import edit from '../../assets/images/edit.png'
import './roles.css'
import { UserType } from '../../data'
import { useCurrentUser } from '../../hooks/useCurrentUser';

const font = 'Outfit'

export default function EditRole({updateUser, onUpdated}) {
  const decode = useCurrentUser();
  const user = null;
  const isBijayClient = decode?.clientId === '98ffb18a-9fee-43a5-91a3-4b2b9d9ae361'

  const [openEditRole, setEditRole] = useState(false)
  const [selectedRole, setSelectedRole] = useState('')
  const [updatedName, setUpdatedName] = useState('')
  const [updatedMobile, setUpdatedMobile] = useState('')
  const [updatedEmail, setUpdatedEmail] = useState('')
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  const handleOpen = () => {
    setUpdatedName(updateUser?.name || '')
    setUpdatedMobile(updateUser?.mobile || '')
    setUpdatedEmail(updateUser?.emailId || '')
    setSelectedRole(updateUser?.role || '')
    setErrors({})
    setEditRole(true)
  }

  const validate = () => {
    const e = {}
    if (!updatedName.trim()) e.name = 'Name is required'
    if (!updatedMobile.trim() || updatedMobile.length < 10) e.mobile = 'Mobile is required and should be at least 10 digits'
    if (!updatedEmail.trim()) e.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(updatedEmail)) e.email = 'Enter a valid email'
    return e
  }

  const handleUpdateInfo = () => {
    if(decode?.clientId !== '98ffb18a-9fee-43a5-91a3-4b2b9d9ae361'){
      setSnackbar({ open: true, message: 'Please contact admin to update this user', severity: 'info' })
      return
    }
    const e = validate()
    if (Object.keys(e).length) { setErrors(e); return }

    if (loading) return
    setLoading(true)

    const body = {
      userId: updateUser?.userId,
      name: updatedName,
      mobile: updatedMobile,
      emailId: updatedEmail,
      role: selectedRole || updateUser?.role,
    }
    axiosInstance.post('/admin/update-info', body, {
    })
    .then(() => {
      setEditRole(false)
      setSnackbar({ open: true, message: 'User updated successfully', severity: 'success' })
      onUpdated?.()
    })
    .catch(() => {
      setSnackbar({ open: true, message: 'Error while updating', severity: 'error' })
    })
    .finally(() => setLoading(false))
  }

  const roleOptions = isBijayClient
    ? [
        { label: 'Admin', value: UserType.Admin },
        { label: 'Executive', value: UserType.Executive },
      ]
    : updateUser?.role !== 'institute_admin' && updateUser?.role !== 'institute_user'
    ? [
        { label: 'Admin', value: UserType.Admin },
        { label: 'Finance', value: UserType.Finance },
        { label: 'CRA', value: UserType.LoanProcessor },
        { label: 'Institute Admin', value: UserType.InstituteAdmin },
        { label: 'Collections', value: UserType.Collection },
      ]
    : [
        { label: 'Institute Admin', value: UserType.InstituteAdmin },
        { label: 'Institute User', value: UserType.InstituteUser },
      ]

  return (
    <div>
      <Dialog
        open={openEditRole}
        onClose={() => setEditRole(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
      >
        {/* Header */}
        <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: '#fff3f3', width: 56, height: 56 }}>
              <EditRoundedIcon sx={{ color: '#d32028', fontSize: 28 }} />
            </Avatar>
            <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: '18px', color: '#1a1a1a' }}>
              Edit User
            </Typography>
            <Typography sx={{ fontFamily: font, fontSize: '13px', color: '#888', fontWeight: 400 }}>
              Update user details below
            </Typography>
          </Box>
        </DialogTitle>

        {/* User info card */}
        <DialogContent sx={{ px: 3, pt: 3, pb: 1, overflow: 'visible' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              size="small"
              fullWidth
              value={updatedName}
              onChange={(e) => { setUpdatedName(e.target.value); setErrors(p => ({ ...p, name: '' })) }}
              error={!!errors.name}
              helperText={errors.name}
              InputProps={{ sx: { fontFamily: font, borderRadius: '8px' } }}
              InputLabelProps={{ sx: { fontFamily: font } }}
              FormHelperTextProps={{ sx: { fontFamily: font } }}
            />
            <TextField
              label="Mobile"
              size="small"
              fullWidth
              value={updatedMobile}
              onChange={(e) => { setUpdatedMobile(e.target.value); setErrors(p => ({ ...p, mobile: '' })) }}
              error={!!errors.mobile}
              helperText={errors.mobile}
              InputProps={{ sx: { fontFamily: font, borderRadius: '8px' } }}
              InputLabelProps={{ sx: { fontFamily: font } }}
              FormHelperTextProps={{ sx: { fontFamily: font } }}
            />
            <TextField
              label="Email"
              size="small"
              fullWidth
              value={updatedEmail}
              onChange={(e) => { setUpdatedEmail(e.target.value); setErrors(p => ({ ...p, email: '' })) }}
              error={!!errors.email}
              helperText={errors.email}
              InputProps={{ sx: { fontFamily: font, borderRadius: '8px' } }}
              InputLabelProps={{ sx: { fontFamily: font } }}
              FormHelperTextProps={{ sx: { fontFamily: font } }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontFamily: font }}>Role</InputLabel>
              <Select
                value={selectedRole || updateUser?.role || ''}
                label="Role"
                onChange={(e) => setSelectedRole(e.target.value)}
                sx={{
                  fontFamily: font,
                  borderRadius: '8px',
                  '& .MuiSelect-select': { fontFamily: font },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '10px',
                      mt: 0.5,
                      boxShadow: '0px 4px 20px rgba(0,0,0,0.08)',
                      '& .MuiMenuItem-root': {
                        fontFamily: font,
                        fontSize: '14px',
                        px: 2,
                        borderRadius: '6px',
                        mx: 0.5,
                        '&:hover': { backgroundColor: '#f5f5f5', color: '#1a1a1a' },
                        '&.Mui-selected': {
                          backgroundColor: '#eeeeee',
                          color: '#1a1a1a',
                          fontWeight: 600,
                          '&:hover': { backgroundColor: '#e0e0e0' },
                        },
                      },
                    },
                  },
                }}
              >
                {roleOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <Divider sx={{ mx: 3 }} />

        {/* Actions */}
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setEditRole(false)}
            sx={{
              textTransform: 'none',
              fontFamily: font,
              fontWeight: 500,
              borderRadius: '8px',
              borderColor: '#ddd',
              color: '#626872',
              '&:hover': { borderColor: '#bbb', backgroundColor: '#f5f5f5' }
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleUpdateInfo}
            disabled={loading}
            sx={{
              textTransform: 'none',
              fontFamily: font,
              fontWeight: 600,
              borderRadius: '8px',
              backgroundColor: '#d32028',
              '&:hover': { backgroundColor: '#b81c22' },
              '&.Mui-disabled': { backgroundColor: '#e57373', color: '#fff' }
            }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <img
        onClick={handleOpen}
        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
        src={edit}
        alt=""
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ fontFamily: font, '& .MuiAlert-message': { fontFamily: font } }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  )
}
